import React, { FC, useEffect, useMemo, useRef } from 'react';
import { Control, useWatch } from 'react-hook-form';

import type { StakeholderType } from '../../../../../types/stakeholder.types';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { useLocale } from '../../../../../hooks/index';
import { EventTypeBackend, GrantEvent } from '../../../../../types/events.types';
import { TemplateSignatoryTypeEntity } from '../../../../documents/templates/components/table/row/template-row.types';
import {
  getTemplateSignatoryBaseType,
  getTemplateSignatoryLabelKey,
  getTemplateSignatoryRoleAliasIndex,
  GRANTEE,
} from '../../../../documents/templates/constants/template-signatory-type.constant';
import { useTemplateById } from '../../../../documents/templates/hooks/use-template-by-id.hook';
import { StakeholderCombobox } from '../../components/index';
import { ChildrenFormProps } from '../../event-form.types';
import { FormSchema } from '../../event-form.validation';
import { getGrantTemplateFixedSigners } from '../../utils/grant-template-fixed-signers.util';

const GRANT_BASE_PATH = 'stepOne.eventDetails.grant';

type SortOrderable = { sortOrder?: null | number | string };

const sortByOrder = (a: SortOrderable, b: SortOrderable): number => {
  const toOrder = (s: SortOrderable) => {
    const n = Number(s.sortOrder);
    return Number.isFinite(n) ? n : 0;
  };
  return toOrder(a) - toOrder(b);
};

type GrantItemForRecipient = {
  stakeholder?: { fullName?: string; id?: string } | null;
};

type ConfirmRecipientCardProps = {
  companyId: string;
  control: Control<FormSchema>;
  duplicateTypeIndex: number;
  duplicateTypeTotal: number;
  fixedStakeholder?: { fullName: string; id: string };
  grantItems: GrantItemForRecipient[];
  index: number;
  signatoryType: TemplateSignatoryTypeEntity;
};

const ConfirmRecipientCard: FC<ConfirmRecipientCardProps> = React.memo(
  ({
    companyId,
    control,
    duplicateTypeIndex,
    duplicateTypeTotal,
    fixedStakeholder,
    grantItems,
    index,
    signatoryType,
  }) => {
    const { messagesLocale } = useLocale();
    const baseSignatoryType = getTemplateSignatoryBaseType(signatoryType.stakeholderType);
    const isGrantee = baseSignatoryType === GRANTEE;
    const isFixedSigner = fixedStakeholder != null;
    const typeMessageId = getTemplateSignatoryLabelKey(signatoryType.stakeholderType);
    const typeLabel = typeMessageId ? messagesLocale[typeMessageId] : signatoryType.stakeholderType;
    const roleAliasIndex = getTemplateSignatoryRoleAliasIndex(signatoryType.stakeholderType);
    const typeLabelWithIndex =
      roleAliasIndex != null
        ? `${typeLabel} ${roleAliasIndex}`
        : duplicateTypeTotal > 1
          ? `${typeLabel} ${duplicateTypeIndex}`
          : typeLabel;

    return (
      <div className="flex flex-col gap-2 rounded-lg border border-gray-25 bg-gray-25 p-4 shadow-[0_-1px_3px_0_rgba(16,24,40,0.04),0_2px_3px_0_rgba(16,24,40,0.10),0_1px_2px_0_rgba(16,24,40,0.06)]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className="flex size-6 shrink-0 items-center justify-center rounded-3xl bg-brand-25 text-xs font-normal text-brand-700"
              title={`${index + 1}`}
            >
              {index + 1}
            </span>
            <span className="text-sm font-medium text-gray-700">{typeLabelWithIndex}</span>
          </div>
          {isGrantee || isFixedSigner ? (
            <span className="flex h-[26px] shrink-0 items-center rounded-3xl bg-forest-50 px-3 text-xs font-medium text-forest-600">
              <AppFormattedMessage id="AUTO_ASSIGNED" />
            </span>
          ) : (
            <StakeholderCombobox
              addButtonLabel={
                <>
                  + <AppFormattedMessage id="ADD_NEW" />
                </>
              }
              companyId={companyId}
              control={control}
              emptyListMessage={
                <AppFormattedMessage id="NO_TYPE_FOUND" values={{ type: typeLabel }} />
              }
              name={`${GRANT_BASE_PATH}.signatoryAssignments.${signatoryType.id}`}
              placeholder={<AppFormattedMessage id="CHOOSE" />}
              type={baseSignatoryType as StakeholderType}
            />
          )}
        </div>
        {(isGrantee || isFixedSigner) && (
          <ul className="-mt-2 flex flex-col gap-[2px] pl-8 text-sm text-gray-400">
            {(isGrantee
              ? grantItems.map((item, idx) => ({
                  id: item.stakeholder?.id ?? `grantee-${idx}`,
                  name: item.stakeholder?.fullName ?? '—',
                }))
              : fixedStakeholder
                ? [{ id: fixedStakeholder.id, name: fixedStakeholder.fullName || '—' }]
                : []
            ).map((stakeholder) => (
              <li className="text-[10px] leading-4" key={stakeholder.id}>
                {stakeholder.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);
ConfirmRecipientCard.displayName = 'ConfirmRecipientCard';

const getAssignedStakeholderId = (assigned: unknown): null | string => {
  if (!assigned || typeof assigned !== 'object' || !('id' in assigned)) return null;
  const id = assigned.id;
  return typeof id === 'string' && id.trim() !== '' ? id : null;
};

const buildGranteeKey = (
  items: Array<{ stakeholder?: { id?: string } | null }> | null | undefined,
): string =>
  (items ?? [])
    .map((item) => item.stakeholder?.id?.trim() ?? '')
    .filter(Boolean)
    .join(',');

export const GrantStepFourth: FC<ChildrenFormProps> = ({
  companyId,
  control,
  event,
  formData,
  handleCloseModal,
  isGrantEditWizard,
  lockMode,
  nextFormStep,
  prevFormStep,
  setFormData,
}) => {
  const lastResetGranteeKeyRef = useRef<null | string>(null);
  const resetContextEventIdRef = useRef<null | string>(null);
  const watchedGrantItems = useWatch({
    control,
    name: `${GRANT_BASE_PATH}.grantItems`,
    defaultValue: [],
  });
  const grantItems = useMemo(() => watchedGrantItems ?? [], [watchedGrantItems]);

  const signatoryAssignments = useWatch({
    control,
    name: `${GRANT_BASE_PATH}.signatoryAssignments`,
    defaultValue: {},
  });
  const selectedTemplateId = useWatch({
    control,
    name: `${GRANT_BASE_PATH}.selectedTemplateId`,
    defaultValue: '',
  });

  const { template } = useTemplateById(selectedTemplateId?.trim() ?? null);
  const fixedSignersBySignatoryId = useMemo(
    () => getGrantTemplateFixedSigners(template),
    [template],
  );

  const processedData = useMemo(() => {
    const ids = grantItems.map((g) => g.stakeholder?.id).filter(Boolean);
    const granteeSet = new Set(ids);
    const sortedTypes = [...(template?.signatoryTypes ?? [])].sort(sortByOrder);
    const nonGranteeTypes = sortedTypes.filter(
      (st) =>
        getTemplateSignatoryBaseType(st.stakeholderType) !== GRANTEE &&
        !fixedSignersBySignatoryId[st.id],
    );
    const hasGranteeWithAdditionalSignerRole = sortedTypes.some((st) => {
      if (getTemplateSignatoryBaseType(st.stakeholderType) === GRANTEE) {
        return false;
      }

      const assignedStakeholderId =
        fixedSignersBySignatoryId[st.id]?.id ??
        getAssignedStakeholderId(signatoryAssignments?.[st.id]);

      return assignedStakeholderId != null && granteeSet.has(assignedStakeholderId);
    });
    const canProceedStatus =
      nonGranteeTypes.length === 0 ||
      nonGranteeTypes.every(
        (st) => getAssignedStakeholderId(signatoryAssignments?.[st.id]) !== null,
      );
    const signatoryTypeCounts: Record<string, number> = {};
    sortedTypes.forEach((st) => {
      const labelKey =
        getTemplateSignatoryLabelKey(st.stakeholderType) ??
        `fallback:${st.stakeholderType.toLowerCase()}`;
      signatoryTypeCounts[labelKey] = (signatoryTypeCounts[labelKey] ?? 0) + 1;
    });
    const signatoryTypeSeen: Record<string, number> = {};
    const signatoryTypeIndexById: Record<string, number> = {};
    const signatoryTypeTotalById: Record<string, number> = {};
    sortedTypes.forEach((st) => {
      const labelKey =
        getTemplateSignatoryLabelKey(st.stakeholderType) ??
        `fallback:${st.stakeholderType.toLowerCase()}`;
      signatoryTypeSeen[labelKey] = (signatoryTypeSeen[labelKey] ?? 0) + 1;
      signatoryTypeIndexById[st.id] = signatoryTypeSeen[labelKey];
      signatoryTypeTotalById[st.id] = signatoryTypeCounts[labelKey];
    });
    return {
      canProceed: canProceedStatus,
      granteeIds: ids,
      granteeSet,
      hasGranteeWithAdditionalSignerRole,
      signatoryTypeIndexById,
      signatoryTypeTotalById,
      sortedTypes,
    };
  }, [fixedSignersBySignatoryId, grantItems, signatoryAssignments, template?.signatoryTypes]);

  useEffect(() => {
    const current = formData(`${GRANT_BASE_PATH}.signatoryAssignments`);
    if (template?.signatoryTypes?.length && current === undefined) {
      setFormData(`${GRANT_BASE_PATH}.signatoryAssignments`, {});
    }
  }, [template?.signatoryTypes?.length, formData, setFormData]);

  useEffect(() => {
    const fixedEntries = Object.entries(fixedSignersBySignatoryId);
    if (fixedEntries.length === 0) return;

    const current = formData(`${GRANT_BASE_PATH}.signatoryAssignments`) ?? {};
    fixedEntries.forEach(([signatoryId, stakeholder]) => {
      const currentId = getAssignedStakeholderId(current[signatoryId]);
      if (currentId == null) {
        setFormData(`${GRANT_BASE_PATH}.signatoryAssignments.${signatoryId}`, stakeholder);
      }
    });
  }, [fixedSignersBySignatoryId, formData, setFormData]);

  useEffect(() => {
    if (!isGrantEditWizard || event?.type !== EventTypeBackend.GRANT) return;

    if (resetContextEventIdRef.current !== event.id) {
      resetContextEventIdRef.current = event.id;
      lastResetGranteeKeyRef.current = null;
    }

    const currentGranteeKey = buildGranteeKey(grantItems);
    const originalGranteeKey = buildGranteeKey((event as GrantEvent).items);
    if (currentGranteeKey === originalGranteeKey) return;
    if (currentGranteeKey === lastResetGranteeKeyRef.current) return;
    if (!template?.signatoryTypes?.length) return;

    lastResetGranteeKeyRef.current = currentGranteeKey;

    const sortedTypes = [...template.signatoryTypes].sort(sortByOrder);
    sortedTypes.forEach((st) => {
      const isGrantee = getTemplateSignatoryBaseType(st.stakeholderType) === GRANTEE;
      const isFixedSigner = fixedSignersBySignatoryId[st.id] != null;
      if (!isGrantee && !isFixedSigner) {
        setFormData(`${GRANT_BASE_PATH}.signatoryAssignments.${st.id}`, null);
      }
    });
  }, [
    event,
    fixedSignersBySignatoryId,
    grantItems,
    isGrantEditWizard,
    setFormData,
    template?.signatoryTypes,
  ]);

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <h2 className="text-xs text-gray-500">
          <AppFormattedMessage id="CONFIRM_RECIPIENTS" />
        </h2>
        {processedData.hasGranteeWithAdditionalSignerRole && (
          <div className="rounded-[6px] bg-sunbeam-50 px-3 py-2 text-xs text-gray-800">
            <AppFormattedMessage id="GRANTEE_ADDITIONAL_SIGNER_INFO" />
          </div>
        )}
        <div className="flex flex-col gap-4">
          {processedData.sortedTypes.map((st, index) => {
            const templateFixed = fixedSignersBySignatoryId[st.id];
            const assigned = signatoryAssignments?.[st.id];
            const assignedId = getAssignedStakeholderId(assigned);
            const fixedStakeholder =
              templateFixed == null
                ? undefined
                : assignedId != null
                  ? {
                      id: assignedId,
                      fullName:
                        typeof assigned === 'object' &&
                        assigned &&
                        'fullName' in assigned &&
                        typeof assigned.fullName === 'string'
                          ? assigned.fullName
                          : templateFixed.fullName,
                    }
                  : templateFixed;

            return (
              <ConfirmRecipientCard
                companyId={companyId}
                control={control}
                duplicateTypeIndex={processedData.signatoryTypeIndexById[st.id] ?? 1}
                duplicateTypeTotal={processedData.signatoryTypeTotalById[st.id] ?? 1}
                fixedStakeholder={fixedStakeholder}
                grantItems={grantItems}
                index={index}
                key={st.id}
                signatoryType={st}
              />
            );
          })}
        </div>
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {(!lockMode || isGrantEditWizard) && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!processedData.canProceed}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode && !isGrantEditWizard ? (
              <AppFormattedMessage id="UPDATE" />
            ) : (
              <AppFormattedMessage id="NEXT" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
};
